import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import FinButton from '../../connect/finance/fin-button';
import FinDropdown from '../../connect/finance/fin-dropdown';

dayjs.extend(utc);

// The Payouts list's four dropdowns and Export (spec 2026-09-21 §5.2).
// Date is the server read window — its own presets, not the section's
// period picker: a payouts list wants "last 90 days", which the statement's
// weekly presets do not offer, and no custom range in v1. Status, Method
// and Amount apply client-side over whatever that window returned; Amount
// is a sort, labelled the way the mock labels it.
export const PAYOUT_DATE_PRESETS = [
  { value: 'last_30_days', label: 'Last 30 days', days: 30 },
  { value: 'last_90_days', label: 'Last 90 days', days: 90 },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
];
export const DEFAULT_DATE = 'last_90_days';

export const STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
];
export const METHOD_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'instant', label: 'Instant' },
];
export const AMOUNT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'highest', label: 'Highest first' },
  { value: 'lowest', label: 'Lowest first' },
];

export const DEFAULT_FILTERS = { status: [], method: [], amount: 'newest', date: DEFAULT_DATE };

// Windows are cut in UTC; `to` never passes now. ISO strings, the shape
// getFinancePayouts sends.
export const resolveDateWindow = (preset, now = dayjs.utc()) => {
  const option = PAYOUT_DATE_PRESETS.find((p) => p.value === preset) || PAYOUT_DATE_PRESETS[1];
  let from;
  let to = now;
  if (option.days) {
    from = now.subtract(option.days - 1, 'day').startOf('day');
  } else if (option.value === 'this_month') {
    from = now.startOf('month');
  } else if (option.value === 'last_month') {
    const last = now.subtract(1, 'month');
    from = last.startOf('month');
    to = last.endOf('month');
  } else {
    from = now.startOf('year');
  }
  if (to.isAfter(now)) to = now;
  return { from: from.toISOString(), to: to.toISOString() };
};

const byDate = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
const SORTS = {
  newest: byDate,
  highest: (a, b) => (b.amount || 0) - (a.amount || 0) || byDate(a, b),
  lowest: (a, b) => (a.amount || 0) - (b.amount || 0) || byDate(a, b),
};

export const applyFilters = (rows, filters) => {
  const status = filters.status || [];
  const method = filters.method || [];
  return rows
    .filter((row) => status.length === 0 || status.includes(row.status))
    .filter((row) => method.length === 0 || (row.method && method.includes(row.method)))
    .sort(SORTS[filters.amount] || SORTS.newest);
};

// "A client filter is active" means a Status or Method box is checked —
// the two that can empty a list. Amount cannot.
export const hasClientFilter = (filters) =>
  (filters.status || []).length > 0 || (filters.method || []).length > 0;

export const emptyCopyFor = (preset) => {
  const option = PAYOUT_DATE_PRESETS.find((p) => p.value === preset);
  return option?.days ? `No payouts in the last ${option.days} days.` : 'No payouts in this period.';
};

const PayoutsFilterBar = ({ filters, onChange, onExport }) => {
  const set = (patch) => onChange({ ...filters, ...patch });
  const statusCount = (filters.status || []).length;
  const methodCount = (filters.method || []).length;
  const amountOff = filters.amount !== DEFAULT_FILTERS.amount;
  const dateOff = filters.date !== DEFAULT_DATE;
  const anyToClear = statusCount > 0 || methodCount > 0 || amountOff;

  return (
    <div className="flex flex-wrap items-center gap-2 px-5 pb-3.5 pt-[18px]">
      <FinDropdown
        mode="checkbox"
        label={statusCount ? `Status · ${statusCount}` : 'Status'}
        active={statusCount > 0}
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(status) => set({ status })}
      />
      <FinDropdown
        mode="radio"
        label={amountOff ? `Amount: ${AMOUNT_OPTIONS.find((o) => o.value === filters.amount)?.label}` : 'Amount'}
        active={amountOff}
        value={filters.amount}
        options={AMOUNT_OPTIONS}
        onChange={(amount) => set({ amount })}
      />
      <FinDropdown
        mode="checkbox"
        label={methodCount ? `Method · ${methodCount}` : 'Method'}
        active={methodCount > 0}
        value={filters.method}
        options={METHOD_OPTIONS}
        onChange={(method) => set({ method })}
      />
      <FinDropdown
        mode="radio"
        aria-label="Date"
        active={dateOff}
        value={filters.date}
        options={PAYOUT_DATE_PRESETS.map(({ value, label }) => ({ value, label }))}
        onChange={(date) => set({ date })}
      />
      {anyToClear ? (
        <button
          type="button"
          className="text-[13px] font-semibold text-fin-blue hover:text-fin-blue-hover"
          onClick={() => set({ status: [], method: [], amount: DEFAULT_FILTERS.amount })}
        >
          Clear filters
        </button>
      ) : null}
      <div className="ml-auto">
        <FinButton onClick={onExport}>Export</FinButton>
      </div>
    </div>
  );
};

export default PayoutsFilterBar;
